import {
  collection,
  addDoc,
  query,
  getDocs,
  serverTimestamp,
  orderBy,
  doc,
  updateDoc,
  deleteDoc,
  getDoc
} from 'firebase/firestore';
import { db } from '../firebaseConfig'; // Importa la instancia db

// --- Firestore Path Helpers --- 
// Helper para obtener la referencia a la colección de notas de un usuario
const getNotesCollectionRef = (userId) => {
  if (!userId) throw new Error("User ID is required to get notes collection reference.");
  return collection(db, `users/${userId}/notes`);
};

// Helper para obtener la referencia a un documento de nota específico
const getNoteDocRef = (userId, noteId) => {
  if (!userId || !noteId) throw new Error("User ID and Note ID are required to get note document reference.");
  return doc(db, `users/${userId}/notes`, noteId);
};


// --- CRUD Functions --- 

/**
 * Agrega una nueva nota a Firestore para el usuario especificado.
 * @param {string} userId - El ID del usuario.
 * @param {object} noteData - Los datos de la nota (e.g., { title: '...', content: '...', tags: [...] }).
 * @returns {Promise<DocumentReference>} Una promesa que resuelve con la referencia al documento creado.
 */
export const addNote = (userId, noteData) => {
  if (!userId) return Promise.reject("User ID is required to add a note.");
  const notesCollection = getNotesCollectionRef(userId);
  return addDoc(notesCollection, {
    title: noteData.title || '',
    content: noteData.content || '',
    tags: noteData.tags || [], // Asegurar que tags sea un array
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
};

/**
 * Obtiene todas las notas de un usuario desde Firestore, ordenadas por fecha de actualización descendente.
 * @param {string} userId - El ID del usuario.
 * @returns {Promise<array>} Una promesa que resuelve con un array de objetos de nota (incluyendo su id).
 */
export const getNotes = async (userId) => {
  if (!userId) return Promise.reject("User ID is required to get notes.");
  const notesCollection = getNotesCollectionRef(userId);
  const q = query(notesCollection, orderBy('updatedAt', 'desc')); 

  try {
    const querySnapshot = await getDocs(q);
    const notes = querySnapshot.docs.map(doc => ({
      id: doc.id, 
      ...doc.data()
    }));
    return notes;
  } catch (error) {
    console.error("Error getting notes: ", error);
    throw error; // Re-lanzar el error para manejo en el componente
  }
};

/**
 * Actualiza una nota existente en Firestore.
 * @param {string} userId - El ID del usuario.
 * @param {string} noteId - El ID de la nota a actualizar.
 * @param {object} updatedData - Los campos a actualizar (e.g., { title: '...', content: '...' }).
 * @returns {Promise<void>} Una promesa que resuelve cuando la actualización se completa.
 */
export const updateNote = (userId, noteId, updatedData) => {
  if (!userId || !noteId) return Promise.reject("User ID and Note ID are required to update a note.");
  const noteRef = getNoteDocRef(userId, noteId);
  // Asegurarse de no enviar undefined
  const dataToUpdate = {
    ...(updatedData.title !== undefined && { title: updatedData.title }),
    ...(updatedData.content !== undefined && { content: updatedData.content }),
    ...(updatedData.tags !== undefined && { tags: updatedData.tags || [] }), // Asegurar que tags sea un array
    updatedAt: serverTimestamp()
  };
  return updateDoc(noteRef, dataToUpdate);
};

/**
 * Elimina una nota de Firestore.
 * @param {string} userId - El ID del usuario.
 * @param {string} noteId - El ID de la nota a eliminar.
 * @returns {Promise<void>} Una promesa que resuelve cuando la eliminación se completa.
 */
export const deleteNote = (userId, noteId) => {
  if (!userId || !noteId) return Promise.reject("User ID and Note ID are required to delete a note.");
  const noteRef = getNoteDocRef(userId, noteId);
  return deleteDoc(noteRef);
};

// --- Opcional: Obtener una sola nota (si se necesita vista detallada) ---
/**
 * Obtiene una nota específica por su ID.
 * @param {string} userId - El ID del usuario.
 * @param {string} noteId - El ID de la nota.
 * @returns {Promise<object|null>} Una promesa que resuelve con el objeto de la nota (incluyendo id) o null si no existe.
 */
export const getNoteById = async (userId, noteId) => {
  if (!userId || !noteId) return Promise.reject("User ID and Note ID are required to get a note.");
  const noteRef = getNoteDocRef(userId, noteId);
  try {
    const docSnap = await getDoc(noteRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() };
    } else {
      console.log("No such note found!");
      return null;
    }
  } catch (error) {
    console.error("Error getting note by ID: ", error);
    throw error;
  }
}; 